/**
 * Pure helpers for the phone Plan (Plan.dc.html and its sheets). They read the
 * same shaped model the desktop grid renders (shape.js#shapePlanScreen) and
 * decide nothing new: the day strip, a day's booking list and the Move sheet's
 * day choices are all derived from model.lanes / model.days.
 */

const LONG_DAY = { Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday", Sun: "Sunday" };

/** The day-of-month number from a model day's `dayMonth` ("12 Oct" -> "12"). */
export function dayNumber(day) {
  return String(day.dayMonth || "").split(" ")[0];
}

export function longDayName(day) {
  return LONG_DAY[day.label] || day.label;
}

/** Every booking on day `index` across all lanes, earliest time first; untimed last. Each carries its lane key for a move. */
export function dayBookings(model, index) {
  const out = [];
  for (const lane of model.lanes || []) {
    for (const tile of (lane.cells && lane.cells[index]) || []) {
      out.push({ tile, laneKey: lane.key, laneName: lane.name });
    }
  }
  return out.sort((a, b) => {
    const ta = a.tile.plannedTime || "99:99";
    const tb = b.tile.plannedTime || "99:99";
    return ta < tb ? -1 : ta > tb ? 1 : String(a.tile.title).localeCompare(String(b.tile.title));
  });
}

export function dayCount(model, index) {
  return dayBookings(model, index).length;
}

/** The count text under a day in the strip: the number, or an en dash when empty. */
export function dayCountText(count) {
  return count ? String(count) : "–";
}

/** "Wednesday 14 Oct · 3 planned". */
export function dayTitle(day, count) {
  return `${longDayName(day)} ${day.dayMonth} · ${count} planned`;
}

/** "Week 42 · 12 Oct – 16 Oct". */
export function weekRangeText(model, weekNo) {
  const first = model.days[0];
  const last = model.days[model.days.length - 1];
  return first && last ? `Week ${weekNo} · ${first.dayMonth} – ${last.dayMonth}` : `Week ${weekNo}`;
}

/** The index of today in the week, else 0. */
export function defaultDayIndex(model) {
  const i = (model.days || []).findIndex((d) => d.isToday);
  return i >= 0 ? i : 0;
}

/** The day a booking sits on, as an index into model.days (-1 when outside the week). */
export function bookingDayIndex(model, tile) {
  return (model.days || []).findIndex((d) => d.date === tile.plannedDate);
}

/** The Move sheet's choices: each day with how many are booked, and whether it is where the booking is now. */
export function moveChoices(model, tile) {
  return (model.days || []).map((day, i) => ({
    day,
    index: i,
    label: `${day.label} ${day.dayMonth}${day.date === tile.plannedDate ? " (now)" : ""}`,
    booked: dayCount(model, i),
    current: day.date === tile.plannedDate,
  }));
}

/** The label of the day-choice buttons on the Book screen: "Mon 12". */
export function bookDayLabel(day) {
  return `${day.label} ${dayNumber(day)}`;
}

/** "now Mon 12 Oct 15:00" for the Move sheet's subtitle. */
export function nowText(day, tile) {
  const when = day ? `${day.label} ${day.dayMonth}` : tile.plannedDate;
  return `now ${when}${tile.plannedTime ? ` ${tile.plannedTime}` : ""}`;
}

/** The ISO week number of a "YYYY-MM-DD" day (0 when it is not a date). */
export function isoWeekOf(dateStr) {
  const parsed = Date.parse(`${String(dateStr).slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(parsed)) return 0;
  const target = new Date(parsed);
  target.setUTCDate(target.getUTCDate() + 4 - (target.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
}
